import i18next from "i18next";
import moment from "moment";

// error_key → ключ перевода (коды из спецификации b2c/ads_v1)
const ERROR_KEYS = {
  support_ticket_not_found: "support_error_ticket_not_found", // 66
  support_validation: "support_error_validation", // 67
  support_ticket_closed: "support_error_ticket_closed", // 68
  support_rate_limited: "support_error_rate_limited", // 69
  support_file_invalid: "support_error_file_invalid", // 70
  support_order_not_found: "support_error_order_not_found", // 71
  support_action_not_allowed: "support_error_action_not_allowed", // 72
  order_not_exist: "support_error_order_not_exist", // 20
  unable_to_send_email: "support_error_email_send", // 31
  too_many_requests: "support_error_too_many_requests", // 32
  b2b_request_failed: "support_error_unavailable", // 38
  support_code_invalid: "support_error_code_invalid", // 44
  support_access_required: "support_error_access_required", // 45
};

const ERROR_CODES = {
  20: "order_not_exist",
  31: "unable_to_send_email",
  32: "too_many_requests",
  38: "b2b_request_failed",
  44: "support_code_invalid",
  45: "support_access_required",
  66: "support_ticket_not_found",
  67: "support_validation",
  68: "support_ticket_closed",
  69: "support_rate_limited",
  70: "support_file_invalid",
  71: "support_order_not_found",
  72: "support_action_not_allowed",
};

const DETAIL_KEYS = {
  support_too_many_files: "support_error_too_many_files",
  support_file_too_large: "support_error_file_too_large",
  support_file_format_not_allowed: "support_error_file_format",
  support_file_upload_failed: "support_error_file_upload",
  reopen_expired: "support_error_reopen_expired",
};

const FIELD_KEYS = {
  subject: "support_subject",
  message: "support_message",
  category: "support_category",
  service_type: "support_service_type",
  order_id: "support_order_number",
  email: "support_access_email",
  code: "support_code_label",
  files: "support_files_add",
};

export const getSupportErrorKey = error => error?.key || ERROR_CODES[error?.code] || null;

/** Понятный текст ошибки по error_key (сырой error_desc не показываем). */
export const supportErrorText = error => {
  const { t } = i18next;
  const key = getSupportErrorKey(error);
  const details = Array.isArray(error?.details) ? error.details : [];

  const detail = details.find(item => DETAIL_KEYS[item]);
  if (detail) return t(DETAIL_KEYS[detail]);

  if (key === "support_validation") {
    const fields = details
      .map(item => String(item).replace(/\.\d+$/, ""))
      .filter((item, index, list) => FIELD_KEYS[item] && list.indexOf(item) === index)
      .map(item => t(FIELD_KEYS[item]));

    if (fields.length) return t("support_error_validation_fields", { fields: fields.join(", ") });
  }

  if (key && ERROR_KEYS[key]) return t(ERROR_KEYS[key]);

  if (error?.status === 429) return t("support_error_too_many_requests");

  return t("support_error_default");
};

export const isSupportAccessError = error => error?.code === 45 || error?.status === 401;

export const isSupportRateLimit = error => error?.status === 429 || error?.code === 32 || error?.code === 69;

const translateOr = (key, fallback) => (i18next.exists(key) ? i18next.t(key) : fallback);

export const supportStatusLabel = status => translateOr(`support_status_${status}`, status || "");

export const supportCategoryLabel = category => translateOr(`support_category_${category}`, category || "");

export const supportServiceLabel = serviceType => translateOr(`support_service_${serviceType}`, serviceType || "");

export const SUPPORT_STATUS_COLORS = {
  new: { color: "#1565c0", background: "rgba(21, 101, 192, 0.1)" },
  in_progress: { color: "#6a1b9a", background: "rgba(106, 27, 154, 0.1)" },
  waiting_customer: { color: "#b45309", background: "rgba(245, 158, 11, 0.18)" },
  closed: { color: "#546e7a", background: "rgba(84, 110, 122, 0.12)" },
};

export const formatSupportDate = value => (value ? moment(value).format("DD.MM.YYYY HH:mm") : "");

const fileExtension = name => (String(name || "").includes(".") ? String(name).split(".").pop().toLowerCase() : "");

/**
 * Проверка файлов по лимитам из meta.limits до отправки (сервер всё равно проверит содержимое).
 * Возвращает текст ошибки или null.
 */
export const validateSupportFiles = (files, limits) => {
  const { t } = i18next;
  const maxFiles = Number(limits?.files_per_message) || 5;
  const maxKb = Number(limits?.file_max_kb) || 10240;
  const extensions = (limits?.file_extensions || []).map(item => String(item).toLowerCase());

  if (files.length > maxFiles) return t("support_file_too_many", { count: maxFiles });

  for (const file of files) {
    if (extensions.length && !extensions.includes(fileExtension(file.name))) {
      return t("support_file_bad_format", { name: file.name });
    }

    if (file.size > maxKb * 1024) {
      return t("support_file_too_large", { name: file.name, size: Math.round((maxKb / 1024) * 10) / 10 });
    }
  }

  return null;
};

export const supportFilesHint = limits => {
  const maxFiles = Number(limits?.files_per_message) || 5;
  const maxKb = Number(limits?.file_max_kb) || 10240;
  const extensions = limits?.file_extensions || [];

  return i18next.t("support_files_hint", {
    count: maxFiles,
    size: Math.round((maxKb / 1024) * 10) / 10,
    ext: extensions.join(", "),
  });
};

export const supportAcceptAttribute = limits =>
  (limits?.file_extensions || []).map(item => `.${String(item).toLowerCase()}`).join(",") || undefined;

export const formatFileSize = size => {
  const bytes = Number(size) || 0;

  if (bytes >= 1024 * 1024) return `${Math.round((bytes / 1024 / 1024) * 10) / 10} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;

  return `${bytes} B`;
};

/** Категории для выбранного типа услуги из meta.service_types (не хардкодим список). */
export const getServiceTypes = meta => Object.keys(meta?.service_types || {});

export const getCategories = (meta, serviceType) => {
  const categories = meta?.service_types?.[serviceType];

  return Array.isArray(categories) ? categories : [];
};

/** Безопасный внутренний путь для возврата после логина. */
export const getSafeRedirect = value => {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//")) return null;

  return value;
};
